import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import axios from "axios";
import BASEUtils from "../../../../@BASE/utils";
import history from "../../../../@history";
import { addSection, removeSection, updateSection } from "./sectionSlice";
import { getCourses } from "../../courses/store/coursesSlice";
import { getLessons } from "../../lessons/store/lessonsSlice";
import { getUsers } from "../../users/store/usersSlice";
import { getPages } from "../../pages/store/pagesSlice";
import { showMessage } from "../../../store/BASE/messageSlice";

export const getSections = createAsyncThunk(
  "sections/getSections",
  async (params, { getState }) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_SERVICE_URL}/api/sections`
    );

    const data = await response.data;

    return { data };
  }
);

export const getComboData = createAsyncThunk(
  "sections/getComboData",
  async (params, { dispatch, getState }) => {
    const courses = await dispatch(getCourses());
    const lessons = await dispatch(getLessons());
    const sections = await dispatch(getSections());
    const users = await dispatch(getUsers());

    const comboData = sections.payload.data.map((section) => {
      let lesson = lessons.payload.data.find(
        (lesson) => lesson.id === section.lesson_id
      );

      let course = courses.payload.data.find(
        (course) => course.id === lesson.course_id
      );



      let creator = users.payload.data.find(
        (user) => user.id === section.creator_id
      );

      return {
        ...section,
        lesson: lesson,
        course: course,
        creator: creator,
      };
    });

    return { comboData };
  }
);

export const getLessonSections = createAsyncThunk(
  "sections/getLessonSections",
  async (id, { dispatch, getState }) => {
    try {
      // send request to get section
      const response = await axios.get(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/sections/lesson/${id}`
      );
      // get response data
      const data = await response.data;

      return { data };
    } catch (error) {
      // if error, redirect to sections page
      history.push({ pathname: `/imi-comments/lesson/${id}` });
      // show error message
      dispatch(showMessage({ message: "Sections Not Found", variant: "error" }));
      // return null
      return null;
    }
  }
);

const sectionsAdapter = createEntityAdapter({});

export const selectSearchText = ({ sections }) => sections.list.searchText;

export const selectComboData = ({ sections }) => sections.list.comboData;

export const { selectAll: selectSections, selectById: selectSectionsById } =
  sectionsAdapter.getSelectors((state) => state.sections.list);

export const selectFilteredSections = createSelector(
  [selectSections, selectSearchText],
  (sections, searchText) => {
    if (searchText.length === 0) {
      return sections;
    }
    return BASEUtils.filterArrayByString(sections, searchText);
  }
);

export const selectGroupedFilteredSections = createSelector(
  [selectFilteredSections],
  (sections, lessonsData) => {
      return sections
        .sort((a, b) =>
          a.title.localeCompare(b.lesson_id, "es", { sensitivity: "base" })
        )
        .reduce((r, e) => {
          // get first letter of title of current element
          const group = e.title;
          // if there is no property in accumulator with this letter create it
          if (!r[group]) r[group] = { group, children: [e] };
          // if there is push current element to children array for that letter
          else r[group].children.push(e);
          // return accumulator
          return r;
        }, {});
  }
);


const sectionsSlice = createSlice({
  name: "sections",
  initialState: sectionsAdapter.getInitialState({
    searchText: "",
    comboData: {},
  }),
  reducers: {
    setSectionsSearchText: {
      reducer: (state, action) => {
        state.searchText = action.payload;
      },
      prepare: (event) => ({ payload: event.target.value || "" }),
    },
  },
  extraReducers: {
    [updateSection.fulfilled]: sectionsAdapter.upsertOne,
    [addSection.fulfilled]: sectionsAdapter.addOne,
    [removeSection.fulfilled]: (state, action) =>
      sectionsAdapter.removeOne(state, action.payload),
    [getSections.fulfilled]: (state, action) => {
      const { data, routeParams } = action.payload;
      sectionsAdapter.setAll(state, data);
      state.searchText = "";
    },
    [getLessonSections.fulfilled]: (state, action) => {
      const { data, routeParams } = action.payload;
      sectionsAdapter.setAll(state, data);
      state.searchText = "";
    },
    [getComboData.fulfilled]: (state, action) => {
      const { comboData, routeParams } = action.payload;
      state.comboData = comboData;
    }
  },
});

export const { setSectionsSearchText } = sectionsSlice.actions;

export const selectSectionEntities = ({ sections }) => sections.list.entities;

export default sectionsSlice.reducer;
